import Button from '@rio-cloud/rio-uikit/Button';
import { useIsAssetAdministrationInMaintenanceToggle } from '../../../../configuration/featureToggle/toggleHooks';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { FormattedMessage } from 'react-intl';
import { useUpdateAssetMutation } from '../../../services/assetAdministrationApi';
import { closableSuccessFormattedNotification } from './ClickableNotifications';
import { BulkActiveContractsWarning } from './BulkActiveContractsWarning';
import { type Asset, AssetStatus } from './types';
import { useIsLatamTenant } from '../../../../configuration/login/loginSlice';
import { useBulkGetActiveContractsQuery } from '../../../services/contractsApi';
import { useCheckActiveContractsBulkQuery } from '../../../services/latamContractsApi';
import { useState } from 'react';

export interface ArchiveAssetConfirmationDialogProps {
    show: boolean;
    assets: Asset[];
    hideBulkArchiveAssetDialog: () => void;
}

export const BulkArchiveAssetConfirmationDialog = (props: ArchiveAssetConfirmationDialogProps) => {
    const isAssetAdminInMaintenance = useIsAssetAdministrationInMaintenanceToggle();
    const assetIds = props.assets.map(asset => asset.id);
    const [updateAsset] = useUpdateAssetMutation();
    const isLatamTenant = useIsLatamTenant() || false;
    const { data: activeContracts } = useBulkGetActiveContractsQuery(assetIds, { skip: isLatamTenant });
    const { data: activeContractsLatam } = useCheckActiveContractsBulkQuery(assetIds, { skip: !isLatamTenant });
    const [checkBoxChecked, setCheckBox] = useState<boolean>(false);
    const visibleItems = props.assets.slice(0, 5);
    const hiddenItems = props.assets.slice(5);

    const bookedServiceIds = new Set([
        ...(activeContracts ?? []).map(c => c.id),
        ...(activeContractsLatam ?? []).map(c => c.id),
    ]);

    const allAssetsHaveBookedServices = assetIds.every(assetId => bookedServiceIds.has(assetId));
    const someAssetHaveBookedServices = assetIds.some(assetId => bookedServiceIds.has(assetId));
    const isContactSupportButtonShown = someAssetHaveBookedServices && isLatamTenant;
    const disableConfirm =
        isAssetAdminInMaintenance ||
        (isLatamTenant && (allAssetsHaveBookedServices || (someAssetHaveBookedServices && !checkBoxChecked)));

    const archiveAssets = async () => {
        if (isAssetAdminInMaintenance) {
            return;
        }
        for (const originalAsset of props.assets.filter(asset => !bookedServiceIds.has(asset.id))) {
            const updatedAsset: Asset = { ...originalAsset, status: AssetStatus.archived };
            await updateAsset(updatedAsset).unwrap();
        }
        closableSuccessFormattedNotification('intl-msg:update.success');
        props.hideBulkArchiveAssetDialog();
    };

    const archiveAssetModalContent = (
        <div>
            <div className='text-size-h4 margin-bottom-20 text-wrap-balance line-height-125rel'>
                <FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.bulk-confirmation-question'} />
            </div>
            <div className=''>
                <ul>
                    {visibleItems.map(asset => (
                        <li key={asset.id}>
                            <strong>{asset.name}</strong>
                        </li>
                    ))}
                    <li>...</li>
                </ul>

                {hiddenItems.length > 0 && (
                    <ExpanderPanel
                        bsStyle='default'
                        title={
                            <>
                                <FormattedMessage
                                    id={'intl-msg:asset.archive-confirmation-dialog.bulk-confirmation-show-all'}
                                />{' '}
                                ({props.assets.length})
                            </>
                        }
                    >
                        <div className='text-size-16'>
                            <ul>
                                {hiddenItems.map(asset => (
                                    <li key={asset.id}>
                                        <strong>{asset.name}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </ExpanderPanel>
                )}
            </div>
            <div className='alert alert-warning display-flex flex-column gap-15'>
                <div className='display-flex gap-10'>
                    <span className='text-color-warning text-size-h4 uikit-icon icon-warning-sign' />
                    <div>
                        <strong className='text-size-16'>
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.remove-data-sources.title'}
                            />
                        </strong>
                        <div>
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.remove-data-sources.hint'}
                            />
                        </div>
                    </div>
                </div>

                {isLatamTenant ? (
                    ''
                ) : (
                    <div className='display-flex gap-10'>
                        <span className='text-color-warning text-size-h4 uikit-icon icon-warning-sign' />
                        <div>
                            <strong className='text-size-16'>
                                <FormattedMessage
                                    id={'intl-msg:asset.delete-confirmation-dialog.content.booked-services.title'}
                                />
                            </strong>
                            <div>
                                <FormattedMessage
                                    id={'intl-msg:asset.delete-confirmation-dialog.content.booked-services.hint'}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <BulkActiveContractsWarning assets={props.assets} deletionMode={false} />

                {isContactSupportButtonShown && (
                    <div style={{ display: 'flex', justifyContent: 'right' }}>
                        <Button
                            bsStyle={Button.PRIMARY}
                            autoTrackingKey='intl-msg:asset.archive-confirmation-dialog.button.contact-support.bulk'
                            onClick={() =>
                                window.open(
                                    encodeURI(
                                        `https://contact-form.rio.cloud/contact?opener=archive assets ${assetIds}`
                                    ),
                                    '_blank'
                                )
                            }
                        >
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.button.contact-support'}
                            />
                        </Button>
                    </div>
                )}

                {isContactSupportButtonShown && !allAssetsHaveBookedServices ? (
                    <Checkbox
                        data-testid='skip-assets-checkbox'
                        checked={checkBoxChecked}
                        onClick={() => setCheckBox(!checkBoxChecked)}
                    >
                        <FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.bulk-confirmation-skip'} />
                    </Checkbox>
                ) : (
                    ''
                )}
            </div>
            <div className='padding-top-20 children-last-margin-bottom-0'>
                <div className='text-bold margin-bottom-5'>
                    <FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.content.reason.question'} />
                </div>
                <p>
                    <FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.content.reason.explanation'} />
                </p>
            </div>
        </div>
    );

    return (
        <ConfirmationDialog
            show={props.show}
            bsSize={'sm'}
            content={archiveAssetModalContent}
            onClickConfirm={archiveAssets}
            onClickCancel={props.hideBulkArchiveAssetDialog}
            cancelButtonText={<FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.button.cancel'} />}
            confirmButtonText={<FormattedMessage id={'intl-msg:asset.archive-confirmation-dialog.button.confirm'} />}
            disableConfirm={disableConfirm}
            useOverflow={true}
        />
    );
};
